// src/screens/JobPaste/components/AddJobModal/AddJobModal.tsx
import { Save } from 'lucide-react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { AutoComplete } from 'primereact/autocomplete';
import { usePpsAccess } from 'hooks/usePpsAccess';
import classes from './addJobModal.module.scss';
import { useAddJobModal, AddJobFormData } from './useAddJobModal';
import { FieldAccessControl } from 'components/AccessControl';
import { useState, useEffect, useRef } from 'react';
import { CompletedJob } from 'services/jobPasteServices';

const AddJobModal = () => {
  const {
    handleSubmit,
    formData,
    errors,
    onClose,
    priorityOptions,
    setErrors,
    setFormData,
    validateForm,
    visibility,
    roomOptions,
    jobTypes,
    statusOptions,
    saveSingleJobLoading,
    jobTypesOptionLoading,
    roomOptionLoading,
    statusOptionsLoading,
    // Completed jobs search functionality
    completedJobsSearchTerm,
    setCompletedJobsSearchTerm,
    completedJobs,
    setCompletedJobs,
    completedJobsLoading,
    completedJobsSearchLoading,
    selectedCompletedJob,
    searchCompletedJobs,
    loadDefaultCompletedJobs,
    prefillFormFromCompletedJob
  } = useAddJobModal();

  const clickSubmit = () => {
    if (validateForm()) {
      handleSubmit(formData);
      // We don't close here immediately, we wait for the operation to complete
      // The onClose will be called after the save operation is completed successfully
    }
  };

  const handleInputChange = (field: keyof AddJobFormData, value: string | Date | null) => {
    if (saveSingleJobLoading) {
      return;
    } // Prevent changes while loading

    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Handle completed jobs search
  const handleCompletedJobsSearch = (event: any) => {
    const searchTerm = event.query || '';

    // Filter the completed jobs based on the search term
    let filteredJobs = completedJobs;

    if (searchTerm.trim()) {
      filteredJobs = completedJobs.filter(
        job =>
          job.jobId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.ndc.toLowerCase().includes(searchTerm.toLowerCase())
      );

      // If no local matches found, search the API
      if (filteredJobs.length === 0) {
        searchCompletedJobs(searchTerm);
        // Don't show any suggestions while searching
        setFilteredSuggestions([]);
        return;
      }
    } else {
      // If no search term, show all jobs
      filteredJobs = completedJobs;
    }

    // Set the filtered suggestions
    const suggestions = filteredJobs.slice(0, 20).map(job => {
      return {
        label: `${job.jobId} - ${job.description}`,
        value: job
      };
    });

    // Set the suggestions for the AutoComplete
    setFilteredSuggestions(suggestions);
  };

  // Handle completed job selection
  const handleCompletedJobSelect = (event: any) => {
    let selectedJob = null;

    // Try multiple ways to get the job
    if (event?.value && event.value.jobId) {
      selectedJob = event.value;
    } else if (event?.jobId) {
      selectedJob = event;
    } else if (event && typeof event === 'object' && event.jobId) {
      selectedJob = event;
    }

    if (selectedJob && selectedJob.jobId) {
      // Set the selected value for AutoComplete
      const selectedOption = {
        label: `${selectedJob.jobId} - ${selectedJob.description}`,
        value: selectedJob
      };
      setSelectedJobValue(selectedOption);

      // Update the search term to show the selected job
      setCompletedJobsSearchTerm(selectedOption.label);

      // Prefill the form
      prefillFormFromCompletedJob(selectedJob);
    }
  };

  // Handle input change for autocomplete
  const handleAutoCompleteChange = (event: any) => {
    const value = event.target.value;

    if (typeof value === 'string') {
      setCompletedJobsSearchTerm(value);
      // Clear selected value when user types
      setSelectedJobValue(null);
    } else if (value && typeof value === 'object') {
      // This might be a selected job object
      if (value.jobId) {
        const selectedOption = {
          label: `${value.jobId} - ${value.description}`,
          value: value
        };
        setSelectedJobValue(selectedOption);
        setCompletedJobsSearchTerm(selectedOption.label);
        handleCompletedJobSelect({ value: value });
      }
    } else {
      // This means a job was selected, clear the search term
      setCompletedJobsSearchTerm('');
      setSelectedJobValue(null);
    }
  };

  // Handle dropdown click to show all jobs
  const handleDropdownClick = () => {
    // Show all jobs when dropdown is clicked
    if (completedJobs.length === 0) {
      setShouldOpenDropdown(true);
      loadDefaultCompletedJobs();
    } else {
      // Trigger search with empty term to show all jobs
      handleCompletedJobsSearch({ query: '' });
    }
  };

  // State for filtered suggestions
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    Array<{ label: string; value: CompletedJob }>
  >([]);
  const [selectedJobValue, setSelectedJobValue] = useState<{
    label: string;
    value: CompletedJob;
  } | null>(null);
  const [shouldOpenDropdown, setShouldOpenDropdown] = useState(false);

  // Ref for AutoComplete component
  const autoCompleteRef = useRef<any>(null);

  // Update filtered suggestions when jobs change
  useEffect(() => {
    if (completedJobs.length > 0) {
      const allJobsOptions = completedJobs.slice(0, 20).map(job => {
        return {
          label: `${job.jobId} - ${job.description}`,
          value: job
        };
      });

      setFilteredSuggestions(allJobsOptions);

      // Open dropdown if it was requested
      if (shouldOpenDropdown && autoCompleteRef.current) {
        setTimeout(() => {
          autoCompleteRef.current.show();
          setShouldOpenDropdown(false);
        }, 100);
      }
    }
  }, [completedJobs, shouldOpenDropdown]); // Removed filteredSuggestions.length dependency to prevent infinite loops

  // Create a custom footer with disabled controls during loading
  const renderFooter = () => {
    return (
      <div className={classes.modalFooter}>
        <Button
          disabled={saveSingleJobLoading}
          onClick={clickSubmit}
          label="Save"
          className={classes.saveButton}
          icon={
            saveSingleJobLoading ? (
              <ProgressSpinner
                style={{ width: '25px', height: '25px' }}
                strokeWidth="8"
                animationDuration="1s"
                className={classes.saveIcon}
              />
            ) : (
              <Save className={classes.saveIcon} />
            )
          }
        />
      </div>
    );
  };

  return (
    <Dialog
      header="Add New Job"
      visible={visibility}
      onHide={() => !saveSingleJobLoading && onClose()}
      className={classes.modal}
      style={{ width: '1050px' }}
      footer={renderFooter()}
      closeOnEscape={!saveSingleJobLoading}
      closable={!saveSingleJobLoading}
      modal
      blockScroll
    >
      <div className={classes.modalContent}>
        {/* Left side - Form fields */}
        <div className={classes.formContainer}>
          <div className={classes.formGroup}>
            <label htmlFor="job_id">Job ID*</label>
            <FieldAccessControl fieldName="job_id">
              <InputText
                id="job_id"
                value={formData.job_id}
                onChange={e => handleInputChange('job_id', e.target.value)}
                placeholder="Enter job ID"
                className={`${classes.inputText} ${errors.job_id ? 'p-invalid' : ''}`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.job_id && <small className="p-error">{errors.job_id}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="priority">Priority*</label>
            <FieldAccessControl fieldName="priority">
              <Dropdown
                id="priority"
                value={formData.priority}
                options={priorityOptions}
                onChange={e => handleInputChange('priority', e.target.value)}
                className={`${classes.dropdown} ${errors.priority ? 'p-invalid' : ''}`}
                placeholder="Select priority"
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.priority && <small className="p-error">{errors.priority}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="status">Status</label>
            <FieldAccessControl fieldName="status">
              <Dropdown
                loading={statusOptionsLoading}
                id="status"
                showClear
                value={formData.status}
                options={statusOptions}
                onChange={e => handleInputChange('status', e.value)}
                className={`${classes.dropdown} ${errors.status ? 'p-invalid' : ''}`}
                placeholder="Select status"
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.status && <small className="p-error">{errors.status}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="room">Room Assignment*</label>
            <FieldAccessControl fieldName="room">
              <Dropdown
                loading={roomOptionLoading}
                id="room"
                value={formData.room}
                options={roomOptions}
                onChange={e => handleInputChange('room', e.value)}
                className={`${classes.dropdown} ${errors.room ? 'p-invalid' : ''}`}
                placeholder="Select room"
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.room && <small className="p-error">{errors.room}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="run_type">Job Type*</label>
            <FieldAccessControl fieldName="run_type">
              <Dropdown
                loading={jobTypesOptionLoading}
                id="run_type"
                value={formData.run_type}
                options={jobTypes}
                onChange={e => handleInputChange('run_type', e.value)}
                className={`${classes.dropdown} ${errors.run_type ? 'p-invalid' : ''}`}
                placeholder="Select job type"
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.run_type && <small className="p-error">{errors.run_type}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="confirmed_date">Confirmed Date*</label>
            <FieldAccessControl fieldName="confirmed_date">
              <Calendar
                id="confirmed_date"
                value={formData.confirmed_date}
                onChange={e => handleInputChange('confirmed_date', e.value || null)}
                className={`${classes.calendar} ${errors.confirmed_date ? 'p-invalid' : ''}`}
                placeholder="Select date"
                disabled={saveSingleJobLoading}
                showIcon
              />
            </FieldAccessControl>
            {errors.confirmed_date && <small className="p-error">{errors.confirmed_date}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="ndc">NDC</label>
            <FieldAccessControl fieldName="ndc">
              <InputText
                id="ndc"
                value={formData.ndc}
                onChange={e => handleInputChange('ndc', e.target.value)}
                placeholder="e.g., 51407-019-01"
                className={`${classes.inputText} ${errors.ndc ? 'p-invalid' : ''}`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.ndc && <small className="p-error">{errors.ndc}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="description">Description*</label>
            <FieldAccessControl fieldName="description">
              <InputText
                id="description"
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                placeholder="Drug name or description"
                className={`${classes.inputText} ${errors.description ? 'p-invalid' : ''}`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.description && <small className="p-error">{errors.description}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="qty_of_containers_to_fill">Containers to Fill*</label>
            <FieldAccessControl fieldName="qty_of_containers_to_fill">
              <InputText
                id="qty_of_containers_to_fill"
                value={formData.qty_of_containers_to_fill}
                onChange={e => handleInputChange('qty_of_containers_to_fill', e.target.value)}
                keyfilter="pint" // Allow only integers
                placeholder="Enter quantity"
                className={`${classes.inputText} ${errors.qty_of_containers_to_fill ? 'p-invalid' : ''
                  }`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.qty_of_containers_to_fill && (
              <small className="p-error">{errors.qty_of_containers_to_fill}</small>
            )}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="qty_per_container">Quantity per Container*</label>
            <FieldAccessControl fieldName="qty_per_container">
              <InputText
                id="qty_per_container"
                value={formData.qty_per_container}
                onChange={e => handleInputChange('qty_per_container', e.target.value)}
                keyfilter="pint" // Allow only integers
                placeholder="Enter quantity"
                className={`${classes.inputText} ${errors.qty_per_container ? 'p-invalid' : ''}`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.qty_per_container && (
              <small className="p-error">{errors.qty_per_container}</small>
            )}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="qty_units_to_package">Units to Package*</label>
            <FieldAccessControl fieldName="qty_units_to_package">
              <InputText
                id="qty_units_to_package"
                value={formData.qty_units_to_package}
                onChange={e => handleInputChange('qty_units_to_package', e.target.value)}
                placeholder="Enter quantity or SUPERBULK"
                className={`${classes.inputText} ${errors.qty_units_to_package ? 'p-invalid' : ''}`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.qty_units_to_package && (
              <small className="p-error">{errors.qty_units_to_package}</small>
            )}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="number_of_bottles_at_shift_start">Bottles at Shift Start</label>
            <FieldAccessControl fieldName="number_of_bottles_at_shift_start">
              <InputText
                id="number_of_bottles_at_shift_start"
                value={formData.number_of_bottles_at_shift_start || ''}
                onChange={e =>
                  handleInputChange('number_of_bottles_at_shift_start', e.target.value)
                }
                keyfilter="pint" // Allow only integers
                placeholder="Enter quantity"
                className={`${classes.inputText} ${errors.number_of_bottles_at_shift_start ? 'p-invalid' : ''
                  }`}
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.number_of_bottles_at_shift_start && (
              <small className="p-error">{errors.number_of_bottles_at_shift_start}</small>
            )}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="job_start_time">Job Start Time</label>
            <FieldAccessControl fieldName="job_start_time">
              <Calendar
                id="job_start_time"
                showTime
                // minDate={new Date()}
                value={formData.job_start_time instanceof Date ? formData.job_start_time : null}
                onChange={e => handleInputChange('job_start_time', e.value || null)}
                hourFormat="12"
                placeholder="Select date time"
                className={`${classes.calendar} ${errors.job_start_time ? 'p-invalid' : ''}`}
                disabled={saveSingleJobLoading}
                showIcon
              />
            </FieldAccessControl>
            {errors.job_start_time && <small className="p-error">{errors.job_start_time}</small>}
          </div>

          <div className={classes.formGroup}>
            <label htmlFor="notes">Notes</label>
            <FieldAccessControl fieldName="notes">
              <InputTextarea
                id="notes"
                value={formData.initial_note}
                onChange={e => handleInputChange('initial_note', e.target.value)}
                placeholder="Add any additional notes here..."
                className={`${classes.inputText} ${errors.initial_note ? 'p-invalid' : ''}`}
                rows={3}
                autoResize
                disabled={saveSingleJobLoading}
              />
            </FieldAccessControl>
            {errors.initial_note && <small className="p-error">{errors.initial_note}</small>}
          </div>
        </div>

        {/* Right side - Completed jobs search */}
        <div className={classes.rightSide}>
          <div className={classes.completedJobsSection}>
            <h3 className={classes.sectionTitle}>Search Completed Jobs</h3>
            <p className={classes.sectionDescription}>
              View recent completed jobs or search to prefill the form data
            </p>

            <div className={classes.searchGroup}>
              <label htmlFor="completedJobsSearch">Search Jobs</label>
              <AutoComplete
                ref={autoCompleteRef}
                id="completedJobsSearch"
                value={completedJobsSearchTerm}
                suggestions={filteredSuggestions}
                completeMethod={handleCompletedJobsSearch}
                onChange={handleAutoCompleteChange}
                onSelect={handleCompletedJobSelect}
                field="label"
                placeholder={
                  completedJobsLoading
                    ? 'Loading jobs...'
                    : 'Click dropdown or search job ID/description...'
                }
                className={classes.autocomplete}
                disabled={saveSingleJobLoading}
                dropdown
                forceSelection={false}
                minLength={0}
                onDropdownClick={handleDropdownClick}
                itemTemplate={item => (
                  <div
                    className={classes.completedJobItem}
                    onClick={() => {
                      handleCompletedJobSelect({ value: item.value });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={classes.jobId}>{item.value.jobId}</div>
                    <div className={classes.jobDescription}>{item.value.description}</div>
                    <div className={classes.jobDetails}>
                      NDC: {item.value.ndc} | Room: {item.value.room}
                    </div>
                  </div>
                )}
              />

              {completedJobsSearchLoading && (
                <div className={classes.searchLoadingIndicator}>
                  <ProgressSpinner style={{ width: '20px', height: '20px' }} strokeWidth="8" />
                  <span>Searching jobs...</span>
                </div>
              )}

              {!completedJobsLoading && completedJobs.length === 0 && (
                <div className={classes.noJobsMessage}>
                  <p>
                    No completed jobs found. Click the dropdown to refresh or try a different
                    search.
                  </p>
                </div>
              )}
            </div>

            {selectedCompletedJob && (
              <div className={classes.selectedJobInfo}>
                <h4>Prefilled from:</h4>
                <div className={classes.jobInfo}>
                  <strong>{selectedCompletedJob.jobId}</strong>
                  <p>{selectedCompletedJob.description}</p>
                  <small>All fields prefilled except status. Job ID appended with "_1"</small>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default observer(AddJobModal);
